import React, { useState, useEffect, useRef } from 'react';
import { getRouteByDate, saveRoute, markStopComplete, completeRoute, getRouteVersions, getDueClients, approveRoute, listMembers } from './api';
import PaymentModal from './PaymentModal';
import { useTranslation } from '../i18n';

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const SLOTS = ['morning', 'afternoon', 'evening'];
const SLOT_ICONS = { morning: '☀️', afternoon: '🌤️', evening: '🌙' };
const SLOT_COLORS = { morning: '#F59E0B', afternoon: '#3B82F6', evening: '#8B5CF6' };
const SLOT_BG = { morning: '#FEF3C7', afternoon: '#EFF6FF', evening: '#F5F3FF' };
const SLOT_TEXT = { morning: '#92400E', afternoon: '#1E40AF', evening: '#5B21B6' };
const MAX_PER_SLOT = 8;

function getLocalDate() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export default function RoutePlanner({ role, routeClients, onRouteEnded, onRemoveFromRoute, onClearRoute, initialDate }) {
  const { t, lang } = useTranslation();
  const locale = lang === 'es' ? 'es-MX' : 'en-US';
  // Shift labels reuse the shared calendar keys (Morning/Mañana, etc.).
  const shiftLabel = (slot) => t(`cal.${slot}`);
  // Owners/managers build & approve routes; crew managers only run their
  // assigned, approved routes (navigate + mark done/paid).
  const canEdit = role !== 'crew_manager';
  const [date, setDate] = useState(initialDate || getLocalDate());

  // Jump to a date handed over from the Calendar tab ("Open in Route tab").
  // Clear the in-memory slots so the new date loads fresh instead of showing
  // the previously-open day's stops.
  useEffect(() => {
    if (!initialDate) return;
    setUnassigned([]);
    setMorningStops([]);
    setAfternoonStops([]);
    setEveningStops([]);
    setDate(initialDate);
  }, [initialDate]);
  const [crewOptions, setCrewOptions] = useState([]);
  const [dayCrewId, setDayCrewId] = useState('');
  const [unassigned, setUnassigned] = useState([]);
  const [morningStops, setMorningStops] = useState([]);
  const [afternoonStops, setAfternoonStops] = useState([]);
  const [eveningStops, setEveningStops] = useState([]);
  const [saving, setSaving] = useState(null);
  const [saved, setSaved] = useState(null);
  const [morningRouteId, setMorningRouteId] = useState(null);
  const [afternoonRouteId, setAfternoonRouteId] = useState(null);
  const [eveningRouteId, setEveningRouteId] = useState(null);
  const [ending, setEnding] = useState(null);
  const [loadingDue, setLoadingDue] = useState(false);
  const [approving, setApproving] = useState(false);
  const [dayApproved, setDayApproved] = useState(false);
  const [mapError, setMapError] = useState('');
  const [mapReady, setMapReady] = useState(false);
  const [paymentStop, setPaymentStop] = useState(null);
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [dragItem, setDragItem] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const mapInitialized = useRef(false);

  const getStops = (slot) => {
    if (slot === 'morning') return morningStops;
    if (slot === 'afternoon') return afternoonStops;
    return eveningStops;
  };

  const setStopsForSlot = (slot, val) => {
    if (slot === 'morning') setMorningStops(val);
    else if (slot === 'afternoon') setAfternoonStops(val);
    else setEveningStops(val);
  };

  const getRouteId = (slot) => {
    if (slot === 'morning') return morningRouteId;
    if (slot === 'afternoon') return afternoonRouteId;
    return eveningRouteId;
  };

  const setRouteIdForSlot = (slot, id) => {
    if (slot === 'morning') setMorningRouteId(id);
    else if (slot === 'afternoon') setAfternoonRouteId(id);
    else setEveningRouteId(id);
  };

  const makeStop = (client, index) => ({
    client_id: client.id,
    clients: client,
    stop_number: index + 1,
    completed: false,
    paid: false,
    amount_charged: parseFloat(client.recurring_amount) || 0,
    amount_paid: 0,
    tip: 0,
    payment_notes: '',
    temp: true
  });

  const getAllStopsOrdered = () => {
    const all = [];
    unassigned.forEach(s => all.push({ ...s, slot: 'unassigned' }));
    morningStops.forEach(s => all.push({ ...s, slot: 'morning' }));
    afternoonStops.forEach(s => all.push({ ...s, slot: 'afternoon' }));
    eveningStops.forEach(s => all.push({ ...s, slot: 'evening' }));
    return all;
  };

  useEffect(() => { // eslint-disable-line react-hooks/exhaustive-deps
    const hasAnyStops =
      morningStops.length > 0 ||
      afternoonStops.length > 0 ||
      eveningStops.length > 0 ||
      unassigned.length > 0;
    if (!hasAnyStops) {
      SLOTS.forEach(slot => loadRoute(date, slot));
    }
    loadVersions();
  }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { // eslint-disable-line react-hooks/exhaustive-deps
    if (routeClients && routeClients.length > 0) {
      const allExistingIds = new Set([
        ...unassigned.map(s => s.client_id),
        ...morningStops.map(s => s.client_id),
        ...afternoonStops.map(s => s.client_id),
        ...eveningStops.map(s => s.client_id)
      ]);
      const toAdd = routeClients
        .filter(c => !allExistingIds.has(c.id))
        .map((c, i) => makeStop(c, i));
      if (toAdd.length > 0) {
        setUnassigned(prev => [...prev, ...toAdd]);
        setSaved(null);
        setDayApproved(false);
      }
    }
  }, [routeClients.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    initMap();
  }, []);

  // Load this location's crew managers for the assignment dropdown (owners/
  // managers only — crew managers don't assign).
  useEffect(() => {
    if (!canEdit) return;
    const activeLoc = (() => { try { return localStorage.getItem('activeLocationId'); } catch (e) { return null; } })();
    listMembers()
      .then((res) => setCrewOptions(
        (res.data || []).filter((m) =>
          m.role === 'crew_manager' && (!activeLoc || m.location_id === activeLoc)
        )
      ))
      .catch(() => setCrewOptions([]));
  }, [canEdit]);

  useEffect(() => { // eslint-disable-line react-hooks/exhaustive-deps
    if (mapReady) {
      const all = getAllStopsOrdered();
      if (all.length > 0) updateMap(all);
      else {
        clearMarkers();
        if (googleMapRef.current) {
          googleMapRef.current.setCenter({ lat: 29.7604, lng: -95.3698 });
          googleMapRef.current.setZoom(11);
        }
      }
    }
  }, [unassigned, morningStops, afternoonStops, eveningStops, mapReady]);

  const loadVersions = async () => {
    try {
      const { data } = await getRouteVersions();
      setVersions(data);
    } catch {
      setVersions([]);
    }
  };

  const loadVersion = async (version) => {
    if (!version.stop_data) return;
    const reconstructed = version.stop_data.map((s, i) => ({
      client_id: s.client_id,
      clients: { id: s.client_id, name: s.client_name, street: s.street, city: s.city },
      stop_number: i + 1,
      completed: false,
      paid: false,
      amount_charged: 0,
      amount_paid: 0,
      tip: 0,
      payment_notes: '',
      temp: true
    }));
    setStopsForSlot(version.time_slot, reconstructed);
    setDate(version.route_date);
    setShowVersions(false);
    setSaved(null);
  };

  const initMap = async () => {
    try {
      const { Loader } = await import('@googlemaps/js-api-loader');
      const loader = new Loader({
        apiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY,
        version: 'weekly',
        libraries: ['places', 'geocoding']
      });
      const google = await loader.load();
      if (mapRef.current && !mapInitialized.current) {
        mapInitialized.current = true;
        googleMapRef.current = new google.maps.Map(mapRef.current, {
          center: { lat: 29.7604, lng: -95.3698 },
          zoom: 11,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true
        });
        infoWindowRef.current = new google.maps.InfoWindow();
        setMapReady(true);
      }
    } catch (err) {
      setMapError(t('route.err_map'));
    }
  };

  const clearMarkers = () => {
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];
  };

  const updateMap = async (allStops) => {
    if (!window.google || !googleMapRef.current) return;
    clearMarkers();
    if (infoWindowRef.current) infoWindowRef.current.close();

    const geocoder = new window.google.maps.Geocoder();
    const bounds = new window.google.maps.LatLngBounds();

    for (let i = 0; i < allStops.length; i++) {
      const stop = allStops[i];
      if (!stop.clients?.street) continue;
      const address = `${stop.clients.street}, ${stop.clients.city}`;
      const color = stop.slot === 'unassigned' ? '#888888' : SLOT_COLORS[stop.slot];
      const letter = LETTERS[i] || String(i + 1);

      await new Promise((resolve) => {
        geocoder.geocode({ address }, (results, status) => {
          if (status === 'OK' && results[0]) {
            const pos = results[0].geometry.location;
            bounds.extend(pos);
            const marker = new window.google.maps.Marker({
              position: pos,
              map: googleMapRef.current,
              label: {
                text: letter,
                color: 'white',
                fontWeight: 'bold',
                fontSize: '12px'
              },
              icon: {
                path: window.google.maps.SymbolPath.CIRCLE,
                fillColor: color,
                fillOpacity: 1,
                strokeColor: '#fff',
                strokeWeight: 2,
                scale: 14
              },
              title: stop.clients.name
            });

            const slotText = stop.slot === 'unassigned'
              ? t('route.unassigned')
              : `${SLOT_ICONS[stop.slot]} ${shiftLabel(stop.slot)}`;
            const stopText = t('route.marker_stop', { letter });

            marker.addListener('click', () => {
              if (infoWindowRef.current) {
                infoWindowRef.current.setContent(`
                  <div style="font-family:-apple-system,sans-serif;padding:4px;min-width:160px">
                    <div style="font-size:14px;font-weight:500;margin-bottom:4px">${stop.clients.name}</div>
                    <div style="font-size:12px;color:#666;margin-bottom:6px">${stop.clients.street}, ${stop.clients.city}</div>
                    <div style="font-size:11px;background:${color};color:white;display:inline-block;padding:2px 8px;border-radius:20px">${slotText}</div>
                    <div style="font-size:11px;color:#888;margin-top:4px">${stopText}</div>
                  </div>
                `);
                infoWindowRef.current.open(googleMapRef.current, marker);
              }
            });

            markersRef.current.push(marker);
          }
          resolve();
        });
      });
    }

    if (markersRef.current.length > 0) {
      googleMapRef.current.fitBounds(bounds);
      if (markersRef.current.length === 1) googleMapRef.current.setZoom(14);
    }
  };

  const loadRoute = async (d, slot) => {
    try {
      const { data } = await getRouteByDate(d, slot);
      if (data && data.route_stops) {
        const sorted = data.route_stops.sort((a, b) => a.stop_number - b.stop_number);
        setStopsForSlot(slot, sorted);
        setRouteIdForSlot(slot, data.id);
        if (data.approved) setDayApproved(true);
        if (data.crew_manager_id) setDayCrewId(data.crew_manager_id);
      } else {
        setRouteIdForSlot(slot, null);
      }
    } catch {
      // don't clear stops on error
    }
  };

  const handleDragStart = (e, item, fromSlot) => {
    setDragItem({ item, fromSlot });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, toSlot) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOver(toSlot);
  };

  const handleDrop = (e, toSlot) => {
    e.preventDefault();
    setDragOver(null);
    if (!dragItem) return;
    const { item, fromSlot } = dragItem;
    if (fromSlot === toSlot) { setDragItem(null); return; }

    if (toSlot !== 'unassigned') {
      const targetStops = getStops(toSlot);
      if (targetStops.length >= MAX_PER_SLOT) {
        alert(t('route.err_slot_full', { shift: shiftLabel(toSlot), max: MAX_PER_SLOT }));
        setDragItem(null);
        return;
      }
    }

    if (fromSlot === 'unassigned') {
      setUnassigned(prev => prev.filter(s => s.client_id !== item.client_id));
    } else {
      setStopsForSlot(fromSlot, getStops(fromSlot).filter(s => s.client_id !== item.client_id));
    }

    if (toSlot === 'unassigned') {
      setUnassigned(prev => [...prev, { ...item, temp: true }]);
    } else {
      setStopsForSlot(toSlot, [...getStops(toSlot), { ...item, temp: true }]);
    }

    setSaved(null);
    setDayApproved(false);
    setDragItem(null);
  };

  const handleTouchAssign = (item, fromSlot, toSlot) => {
    if (fromSlot === toSlot) return;
    if (toSlot !== 'unassigned') {
      const targetStops = getStops(toSlot);
      if (targetStops.length >= MAX_PER_SLOT) {
        alert(t('route.err_slot_full', { shift: shiftLabel(toSlot), max: MAX_PER_SLOT }));
        return;
      }
    }
    if (fromSlot === 'unassigned') {
      setUnassigned(prev => prev.filter(s => s.client_id !== item.client_id));
    } else {
      setStopsForSlot(fromSlot, getStops(fromSlot).filter(s => s.client_id !== item.client_id));
    }
    if (toSlot === 'unassigned') {
      setUnassigned(prev => [...prev, { ...item, temp: true }]);
    } else {
      setStopsForSlot(toSlot, [...getStops(toSlot), { ...item, temp: true }]);
    }
    setSaved(null);
    setDayApproved(false);
  };

  const removeFromAll = (clientId) => {
    setUnassigned(prev => prev.filter(s => s.client_id !== clientId));
    SLOTS.forEach(slot => {
      setStopsForSlot(slot, getStops(slot).filter(s => s.client_id !== clientId));
    });
    if (onRemoveFromRoute) onRemoveFromRoute(clientId);
    setSaved(null);
    setDayApproved(false);
  };

  const moveStop = (slot, index, direction) => {
    const slotStops = [...getStops(slot)];
    const target = index + direction;
    if (target < 0 || target >= slotStops.length) return;
    [slotStops[index], slotStops[target]] = [slotStops[target], slotStops[index]];
    setStopsForSlot(slot, slotStops);
    setSaved(null);
    setDayApproved(false);
  };

  const handleSave = async (slot) => {
    const slotStops = getStops(slot);
    if (slotStops.length === 0) {
      alert(t('route.err_add_stop_save'));
      return;
    }
    setSaving(slot);
    try {
      const response = await saveRoute(date, slotStops.map(s => s.client_id), slot, dayCrewId || null);
      const fullRoute = response?.data?.route;
      if (fullRoute && fullRoute.route_stops && fullRoute.route_stops.length > 0) {
        const sorted = fullRoute.route_stops.sort((a, b) => a.stop_number - b.stop_number);
        setStopsForSlot(slot, sorted);
        setRouteIdForSlot(slot, fullRoute.id);
      } else {
        const { data } = await getRouteByDate(date, slot);
        if (data && data.route_stops) {
          const sorted = data.route_stops.sort((a, b) => a.stop_number - b.stop_number);
          setStopsForSlot(slot, sorted);
          setRouteIdForSlot(slot, data.id);
        }
      }
      await loadVersions();
      setSaved(slot);
      setTimeout(() => setSaved(null), 3000);
    } catch (err) {
      console.error('Save error:', err);
      alert(t('route.err_save_route'));
    }
    setSaving(null);
  };

  // Pull every client whose next service date is due on/before the selected
  // date into the Unassigned pool, so the day's route can be auto-built.
  const loadDueClients = async () => {
    setLoadingDue(true);
    try {
      const { data } = await getDueClients(date);
      const existing = new Set([
        ...unassigned, ...morningStops, ...afternoonStops, ...eveningStops
      ].map(s => s.client_id));
      const toAdd = (data || []).filter(c => !existing.has(c.id)).map((c, i) => makeStop(c, i));
      if (toAdd.length > 0) {
        setUnassigned(prev => [...prev, ...toAdd]);
        setSaved(null);
        setDayApproved(false);
      } else {
        alert(t('route.err_none_due_route'));
      }
    } catch {
      alert(t('route.err_load_due'));
    }
    setLoadingDue(false);
  };

  // Approve the day: every client must be in a shift (nothing left unassigned).
  // Saves each non-empty shift (preserving the drag-drop order) then approves
  // it, which unlocks navigation and the ordered map.
  const handleApprove = async () => {
    if (unassigned.length > 0) {
      alert(t('route.err_assign_all'));
      return;
    }
    const assignedCount = morningStops.length + afternoonStops.length + eveningStops.length;
    if (assignedCount === 0) {
      alert(t('route.err_add_stop_approve'));
      return;
    }
    setApproving(true);
    try {
      for (const slot of SLOTS) {
        const stops = getStops(slot);
        if (stops.length === 0) continue;
        const resp = await saveRoute(date, stops.map(s => s.client_id), slot, dayCrewId || null);
        const full = resp?.data?.route;
        let routeId = getRouteId(slot);
        if (full && full.route_stops) {
          const sorted = full.route_stops.sort((a, b) => a.stop_number - b.stop_number);
          setStopsForSlot(slot, sorted);
          setRouteIdForSlot(slot, full.id);
          routeId = full.id;
        }
        if (routeId) await approveRoute(routeId);
      }
      setDayApproved(true);
      setSaved(null);
      await loadVersions();
    } catch (err) {
      console.error('Approve error:', err);
      alert(t('route.err_approve'));
    }
    setApproving(false);
  };

  const handleEndRoute = async (slot) => {
    const routeId = getRouteId(slot);
    if (!routeId) {
      alert(t('route.err_save_first_end'));
      return;
    }
    if (!window.confirm(t('route.confirm_end_shift', { shift: shiftLabel(slot) }))) return;
    setEnding(slot);
    try {
      await completeRoute(routeId);
      setStopsForSlot(slot, []);
      setRouteIdForSlot(slot, null);
      setSaved(null);
      clearMarkers();
      if (infoWindowRef.current) infoWindowRef.current.close();
      await loadVersions();
    } catch {
      alert(t('route.err_end'));
    }
    setEnding(null);
  };

  const handleComplete = async (stop, slot) => {
    if (stop.temp) {
      alert(t('route.err_save_first_complete'));
      return;
    }
    try {
      await markStopComplete(stop.id, !stop.completed);
      setStopsForSlot(slot, getStops(slot).map(s =>
        s.id === stop.id ? { ...s, completed: !s.completed } : s
      ));
    } catch {
      alert(t('route.err_update_stop'));
    }
  };

  const handlePaymentUpdated = (updatedStop, slot) => {
    setStopsForSlot(slot, getStops(slot).map(s =>
      s.id === updatedStop.id ? { ...s, ...updatedStop } : s
    ));
  };

  const startNavigation = (slot) => {
    const slotStops = getStops(slot);
    if (!slotStops.length) return;
    if (slotStops.length === 1) {
      const dest = encodeURIComponent(`${slotStops[0].clients.street}, ${slotStops[0].clients.city}`);
      window.open(
        `https://www.google.com/maps/dir/?api=1&origin=My+Location&destination=${dest}&travelmode=driving`,
        '_blank'
      );
      return;
    }
    const destination = encodeURIComponent(
      `${slotStops[slotStops.length - 1].clients.street}, ${slotStops[slotStops.length - 1].clients.city}`
    );
    const waypoints = slotStops.slice(0, -1)
      .map(s => encodeURIComponent(`${s.clients.street}, ${s.clients.city}`))
      .join('|');
    window.open(
      `https://www.google.com/maps/dir/?api=1&origin=My+Location&destination=${destination}&waypoints=${waypoints}&travelmode=driving`,
      '_blank'
    );
  };

  const handleClearAll = () => {
    if (!window.confirm(t('route.confirm_clear_all'))) return;
    setUnassigned([]);
    setMorningStops([]);
    setAfternoonStops([]);
    setEveningStops([]);
    setSaved(null);
    setDayApproved(false);
    clearMarkers();
    if (infoWindowRef.current) infoWindowRef.current.close();
    if (onClearRoute) onClearRoute();
  };

  const fmt = (n) => `$${parseFloat(n || 0).toFixed(2)}`;
  const allAssigned = [...morningStops, ...afternoonStops, ...eveningStops];
  const totalCharged = allAssigned.reduce((s, stop) => s + (parseFloat(stop.amount_charged) || 0), 0);
  const totalCollected = allAssigned.reduce((s, stop) => s + (parseFloat(stop.amount_paid) || 0) + (parseFloat(stop.tip) || 0), 0);

  const formatVersionLabel = (v) => {
    const routeDate = new Date(v.route_date + 'T12:00:00').toLocaleDateString(locale, {
      month: 'short', day: 'numeric', year: 'numeric'
    });
    const savedDate = new Date(v.saved_at).toLocaleDateString(locale, {
      month: 'short', day: 'numeric'
    });
    const savedTime = new Date(v.saved_at).toLocaleTimeString(locale, {
      hour: 'numeric', minute: '2-digit'
    });
    return `${routeDate} · ${shiftLabel(v.time_slot)} · ${t('route.version_saved')} ${savedDate} ${savedTime} · v${v.version_number}`;
  };

  const renderClientCard = (stop, fromSlot, globalIndex) => {
    const charged = parseFloat(stop.amount_charged) || 0;
    const paid = parseFloat(stop.amount_paid) || 0;
    const tip = parseFloat(stop.tip) || 0;
    const balance = charged - paid - tip;
    const letter = LETTERS[globalIndex] || String(globalIndex + 1);
    const color = fromSlot === 'unassigned' ? '#888' : SLOT_COLORS[fromSlot];
    const slotStops = fromSlot !== 'unassigned' ? getStops(fromSlot) : [];
    const localIndex = slotStops.findIndex(s => s.client_id === stop.client_id);

    return (
      <div
        key={stop.client_id}
        draggable={canEdit}
        onDragStart={canEdit ? (e => handleDragStart(e, stop, fromSlot)) : undefined}
        style={{
          ...styles.stopCard,
          ...(stop.completed ? styles.stopDone : {}),
          ...(stop.paid ? styles.stopPaid : {}),
          ...(balance > 0 && charged > 0 ? styles.stopOwes : {}),
          cursor: canEdit ? 'grab' : 'default',
          opacity: dragItem?.item?.client_id === stop.client_id ? 0.5 : 1
        }}
      >
        <div style={{ ...styles.stopNum, background: color }}>{letter}</div>
        <div style={styles.stopInfo}>
          <div style={styles.stopName}>{stop.clients?.name}</div>
          <div style={styles.stopAddr}>{stop.clients?.street}, {stop.clients?.city}</div>
          {stop.clients?.notes && <div style={styles.stopNotes}>{stop.clients.notes}</div>}
          {charged > 0 && (
            <div style={styles.paymentSummary}>
              <span>{t('route.amt_due', { amt: fmt(charged) })}</span>
              {paid + tip > 0 && (
                <span style={{ color: '#3B6D11' }}> · {t('route.amt_collected', { amt: fmt(paid + tip) })}</span>
              )}
              {balance > 0 && (
                <span style={{ color: '#dc2626' }}> · {t('route.amt_balance', { amt: fmt(balance) })}</span>
              )}
            </div>
          )}
          <div style={styles.stopBtns}>
            {fromSlot !== 'unassigned' && (
              <button
                style={{ ...styles.actionBtn, ...(stop.completed ? styles.doneActive : {}) }}
                aria-pressed={!!stop.completed}
                aria-label={t('a11y.mark_done')}
                onClick={() => handleComplete(stop, fromSlot)}
              >
                {stop.completed ? <><span aria-hidden="true">✓ </span>{t('route.done')}</> : t('route.done')}
              </button>
            )}
            {fromSlot !== 'unassigned' && (
              <button
                style={{ ...styles.actionBtn, ...styles.payBtn }}
                aria-label={t('a11y.open_payment')}
                onClick={() => {
                  if (stop.temp) { alert(t('route.err_save_first')); return; }
                  setPaymentStop({ ...stop, slot: fromSlot });
                }}
              >
                <span aria-hidden="true">$ </span>{t('route.pay')}
              </button>
            )}
            {canEdit && (fromSlot === 'unassigned' ? (
              SLOTS.map(slot => (
                <button
                  key={slot}
                  style={{ ...styles.actionBtn, fontSize: 10, padding: '3px 6px' }}
                  aria-label={t('a11y.assign_to_shift', { shift: shiftLabel(slot) })}
                  onClick={() => handleTouchAssign(stop, 'unassigned', slot)}
                >
                  <span aria-hidden="true">{SLOT_ICONS[slot]} </span>{shiftLabel(slot)}
                </button>
              ))
            ) : (
              <button
                style={{ ...styles.actionBtn, fontSize: 10 }}
                aria-label={t('a11y.unassign')}
                onClick={() => handleTouchAssign(stop, fromSlot, 'unassigned')}
              >
                <span aria-hidden="true">← </span>{t('route.unassign')}
              </button>
            ))}
          </div>
        </div>
        {canEdit && (
          <div style={styles.stopActions} role="group" aria-label={t('a11y.client_actions_for', { name: stop.clients?.name || t('route.client_default') })}>
            {fromSlot !== 'unassigned' && localIndex >= 0 && (
              <>
                <button
                  style={styles.iconBtn}
                  aria-label={t('a11y.move_up')}
                  onClick={() => moveStop(fromSlot, localIndex, -1)}
                  disabled={localIndex === 0}
                ><span aria-hidden="true">↑</span></button>
                <button
                  style={styles.iconBtn}
                  aria-label={t('a11y.move_down')}
                  onClick={() => moveStop(fromSlot, localIndex, 1)}
                  disabled={localIndex === slotStops.length - 1}
                ><span aria-hidden="true">↓</span></button>
              </>
            )}
            <button
              style={{ ...styles.iconBtn, ...styles.removeBtn }}
              aria-label={t('a11y.remove_stop')}
              onClick={() => removeFromAll(stop.client_id)}
            ><span aria-hidden="true">✕</span></button>
          </div>
        )}
      </div>
    );
  };

  const renderSlot = (slot) => {
    const slotStops = getStops(slot);
    const slotRouteId = getRouteId(slot);
    const slotCharged = slotStops.reduce((s, stop) => s + (parseFloat(stop.amount_charged) || 0), 0);
    const slotCollected = slotStops.reduce((s, stop) => s + (parseFloat(stop.amount_paid) || 0) + (parseFloat(stop.tip) || 0), 0);
    const slotDone = slotStops.filter(s => s.completed).length;
    const isOver = dragOver === slot;
    const allOrdered = getAllStopsOrdered();

    return (
      <div
        key={slot}
        role="region"
        aria-label={t('a11y.drop_zone_shift', { shift: shiftLabel(slot) })}
        style={{ ...styles.slotSection, ...(isOver ? styles.slotOver : {}) }}
        onDragOver={e => handleDragOver(e, slot)}
        onDragLeave={() => setDragOver(null)}
        onDrop={e => handleDrop(e, slot)}
      >
        <div style={{ ...styles.slotHeader, background: SLOT_BG[slot] }}>
          <div style={styles.slotHeaderLeft}>
            <span style={styles.slotIcon}>{SLOT_ICONS[slot]}</span>
            <span style={{ ...styles.slotLabel, color: SLOT_TEXT[slot] }}>{shiftLabel(slot)}</span>
            <span style={{ ...styles.slotCount, background: SLOT_COLORS[slot] }}>
              {slotStops.length}/{MAX_PER_SLOT}
            </span>
            {slotStops.length > 0 && (
              <span style={{ fontSize: 11, color: SLOT_TEXT[slot] }}>· {t('route.n_done', { n: slotDone })}</span>
            )}
          </div>
          {slotStops.length > 0 && (
            <div style={styles.slotHeaderRight}>
              <button
                style={{
                  ...styles.slotNavBtn,
                  background: dayApproved ? SLOT_COLORS[slot] : '#cbd0c4',
                  cursor: dayApproved ? 'pointer' : 'not-allowed'
                }}
                aria-label={t('route.nav_shift_aria', { shift: shiftLabel(slot) })}
                aria-disabled={!dayApproved}
                onClick={() => dayApproved ? startNavigation(slot) : alert(t('route.err_approve_first_nav'))}
                title={dayApproved ? t('route.nav_title_enabled') : t('route.nav_title_disabled')}
              >
                {t('route.navigate')}
              </button>
              {canEdit && (
                <button
                  style={styles.slotSaveBtn}
                  aria-label={t('route.save_shift_aria', { shift: shiftLabel(slot) })}
                  onClick={() => handleSave(slot)}
                  disabled={saving === slot}
                >
                  {saving === slot ? '...' : saved === slot ? <span aria-label={t('route.version_saved')} role="img">✓</span> : t('route.save')}
                </button>
              )}
            </div>
          )}
        </div>

        {slotStops.length > 0 && slotCharged > 0 && (
          <div style={styles.slotTotals}>
            <span style={{ fontSize: 12, color: '#5f6b58' }}>{t('route.amt_due', { amt: fmt(slotCharged) })}</span>
            {slotCollected > 0 && (
              <span style={{ fontSize: 12, color: '#3B6D11' }}> · {t('route.amt_collected', { amt: fmt(slotCollected) })}</span>
            )}
            {slotCharged - slotCollected > 0 && (
              <span style={{ fontSize: 12, color: '#dc2626' }}>
                {' '}· {t('route.amt_outstanding', { amt: fmt(slotCharged - slotCollected) })}
              </span>
            )}
          </div>
        )}

        {slotStops.length === 0 && (
          <div style={{ ...styles.slotEmpty, ...(isOver ? styles.slotEmptyOver : {}) }}>
            {isOver
              ? t('route.drop_here_shift', { shift: shiftLabel(slot) })
              : t('route.empty_shift_hint')}
          </div>
        )}

        {slotStops.map((stop) => {
          const globalIndex = allOrdered.findIndex(s => s.client_id === stop.client_id);
          return renderClientCard(stop, slot, globalIndex);
        })}

        {slotStops.length > 0 && slotRouteId && (
          <button
            style={styles.endRouteBtn}
            onClick={() => handleEndRoute(slot)}
            disabled={ending === slot}
          >
            {ending === slot ? t('route.ending') : `🏁 ${t('route.end_shift', { shift: shiftLabel(slot) })}`}
          </button>
        )}
      </div>
    );
  };

  const allOrdered = getAllStopsOrdered();

  return (
    <div>
      <div ref={mapRef} style={styles.map} role="region" aria-label={t('route.map_aria')} />
      {mapError && <p style={styles.mapError} role="alert">{mapError}</p>}

      <div style={styles.controls}>
        <div style={styles.topRow}>
          <label htmlFor="route-date" style={styles.srOnly}>{t('a11y.select_date')}</label>
          <input
            id="route-date"
            type="date"
            value={date}
            onChange={e => { setDate(e.target.value); setSaved(null); setDayApproved(false); setDayCrewId(''); }}
            style={styles.dateInput}
            aria-label={t('a11y.select_date')}
          />
          {canEdit && <div style={styles.versionsWrapper}>
            <button
              style={styles.versionsBtn}
              onClick={() => setShowVersions(!showVersions)}
              aria-haspopup="listbox"
              aria-expanded={showVersions}
              aria-label={t('a11y.toggle_versions')}
            >
              {t('route.saved_menu')} <span aria-hidden="true">▾</span>
            </button>
            {showVersions && (
              <div style={styles.versionsDropdown} role="listbox" aria-label={t('a11y.toggle_versions')}>
                {versions.length === 0 && (
                  <div style={styles.versionEmpty}>{t('route.no_versions')}</div>
                )}
                {versions.map(v => (
                  <button key={v.id} role="option" aria-selected="false" style={styles.versionItem} onClick={() => loadVersion(v)}>
                    <span style={styles.versionLabel}>{formatVersionLabel(v)}</span>
                    {v.completed && <span style={styles.versionDone} aria-label={t('route.done')}><span aria-hidden="true">✓</span></span>}
                  </button>
                ))}
              </div>
            )}
          </div>}
        </div>

        {canEdit ? (
          <div style={styles.approveBar}>
            <button style={styles.loadDueBtn} onClick={loadDueClients} disabled={loadingDue}>
              {loadingDue ? t('route.loading') : <><span aria-hidden="true">↻ </span>{t('route.load_due')}</>}
            </button>
            {crewOptions.length > 0 && (
              <>
                <label htmlFor="route-crew" style={styles.srOnly}>{t('a11y.select_crew')}</label>
                <select
                  id="route-crew"
                  style={styles.crewSelect}
                  aria-label={t('a11y.select_crew')}
                  value={dayCrewId}
                  onChange={e => { setDayCrewId(e.target.value); setDayApproved(false); }}
                >
                  <option value="">{t('route.unassigned_crew')}</option>
                  {crewOptions.map(c => (
                    <option key={c.id} value={c.id}>{c.member_name || c.email || t('route.crew')}</option>
                  ))}
                </select>
              </>
            )}
            {dayApproved ? (
              <span style={styles.approvedBadge} role="status" aria-live="polite"><span aria-hidden="true">✓ </span>{t('route.approved_ready')}</span>
            ) : (
              <button
                style={{
                  ...styles.approveBtn,
                  ...((unassigned.length > 0 || allAssigned.length === 0) ? styles.approveBtnDisabled : {})
                }}
                onClick={handleApprove}
                disabled={approving || unassigned.length > 0 || allAssigned.length === 0}
              >
                {approving
                  ? t('route.approving')
                  : unassigned.length > 0
                    ? t('route.assign_n_to_shift', { n: unassigned.length })
                    : <><span aria-hidden="true">✓ </span>{t('route.approve_route')}</>}
              </button>
            )}
          </div>
        ) : (
          <div style={styles.approveBar}>
            <span style={dayApproved ? styles.approvedBadge : styles.pendingBadge} role="status" aria-live="polite">
              {dayApproved ? <><span aria-hidden="true">✓ </span>{t('route.approved_ready')}</> : <><span aria-hidden="true">⏳ </span>{t('route.waiting_approval')}</>}
            </span>
          </div>
        )}

        {totalCharged > 0 && (
          <div style={styles.dayTotal}>
            <div style={styles.dayTotalItem}>
              <div style={styles.dayTotalLabel}>{t('route.total_due')}</div>
              <div style={styles.dayTotalValue}>{fmt(totalCharged)}</div>
            </div>
            <div style={styles.dayTotalItem}>
              <div style={styles.dayTotalLabel}>{t('route.collected')}</div>
              <div style={{ ...styles.dayTotalValue, color: '#3B6D11' }}>{fmt(totalCollected)}</div>
            </div>
            <div style={styles.dayTotalItem}>
              <div style={styles.dayTotalLabel}>{t('route.outstanding')}</div>
              <div style={{
                ...styles.dayTotalValue,
                color: totalCharged - totalCollected > 0 ? '#dc2626' : '#3B6D11'
              }}>
                {fmt(Math.max(0, totalCharged - totalCollected))}
              </div>
            </div>
          </div>
        )}
      </div>

      {unassigned.length > 0 && (
        <div
          role="region"
          aria-label={t('a11y.drop_zone_shift', { shift: t('route.unassigned') })}
          style={{
            ...styles.unassignedSection,
            ...(dragOver === 'unassigned' ? styles.slotOver : {})
          }}
          onDragOver={e => handleDragOver(e, 'unassigned')}
          onDragLeave={() => setDragOver(null)}
          onDrop={e => handleDrop(e, 'unassigned')}
        >
          <div style={styles.unassignedHeader}>
            <span style={styles.unassignedTitle}><span aria-hidden="true">📋 </span>{t('route.unassigned_title', { n: unassigned.length })}</span>
            <span style={styles.unassignedHint}>{t('route.unassigned_hint')}</span>
          </div>
          {unassigned.map((stop) => {
            const globalIndex = allOrdered.findIndex(s => s.client_id === stop.client_id);
            return renderClientCard(stop, 'unassigned', globalIndex);
          })}
        </div>
      )}

      {renderSlot('morning')}
      {renderSlot('afternoon')}
      {renderSlot('evening')}

      {canEdit && (unassigned.length > 0 || allAssigned.length > 0) && (
        <button style={styles.clearAllBtn} onClick={handleClearAll}>
          {t('route.clear_all')}
        </button>
      )}

      {paymentStop && (
        <PaymentModal
          stop={paymentStop}
          onClose={() => setPaymentStop(null)}
          onUpdated={(updatedStop) => {
            handlePaymentUpdated(updatedStop, paymentStop.slot);
            setPaymentStop(null);
          }}
        />
      )}
    </div>
  );
}

const styles = {
  map: { width: '100%', height: 280, borderRadius: 10, marginBottom: 0, background: '#f0f0f0', minHeight: 280, display: 'block' },
  mapError: { color: '#dc2626', fontSize: 12, margin: '4px 0' },
  controls: { padding: '12px 0 0' },
  topRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, position: 'relative' },
  dateInput: { padding: '6px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, flex: 1 },
  versionsWrapper: { position: 'relative' },
  versionsBtn: { padding: '6px 12px', border: '1px solid #ddd', background: '#fff', borderRadius: 8, fontSize: 12, cursor: 'pointer', color: '#5f6b58', whiteSpace: 'nowrap' },
  versionsDropdown: { position: 'absolute', right: 0, top: 36, background: '#fff', border: '1px solid #eee', borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 200, minWidth: 300, maxHeight: 280, overflowY: 'auto' },
  versionEmpty: { padding: '14px 16px', fontSize: 13, color: '#5f6b58' },
  versionItem: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '10px 14px', border: 'none', background: 'none', textAlign: 'left', fontSize: 12, cursor: 'pointer', color: '#1a1a1a', borderBottom: '1px solid #f5f5f5', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', gap: 8 },
  versionLabel: { flex: 1, lineHeight: 1.4 },
  versionDone: { fontSize: 11, color: '#3B6D11', flexShrink: 0 },
  approveBar: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' },
  loadDueBtn: { padding: '8px 14px', border: '1px solid #cfe9d4', background: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#14653a', cursor: 'pointer' },
  approveBtn: { flex: 1, minWidth: 140, padding: '9px 14px', border: 'none', background: '#14653a', borderRadius: 8, fontSize: 13.5, fontWeight: 700, color: '#fff', cursor: 'pointer' },
  approveBtnDisabled: { background: '#cbd0c4', cursor: 'not-allowed' },
  approvedBadge: { flex: 1, minWidth: 140, textAlign: 'center', padding: '9px 14px', background: '#ecf8ee', color: '#14653a', borderRadius: 8, fontSize: 13, fontWeight: 700, border: '1px solid #cfe9d4' },
  pendingBadge: { flex: 1, minWidth: 140, textAlign: 'center', padding: '9px 14px', background: '#fef3c7', color: '#92400e', borderRadius: 8, fontSize: 13, fontWeight: 700, border: '1px solid #fde68a' },
  crewSelect: { padding: '8px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13, background: '#fff', color: '#1a1a1a', maxWidth: 160 },
  dayTotal: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 },
  dayTotalItem: { background: '#f9f9f9', borderRadius: 8, padding: '8px 10px', textAlign: 'center' },
  dayTotalLabel: { fontSize: 10, color: '#5f6b58', marginBottom: 3 },
  dayTotalValue: { fontSize: 15, fontWeight: 500, color: '#1a1a1a' },
  unassignedSection: { border: '2px dashed #ddd', borderRadius: 10, padding: '10px', marginBottom: 12, background: '#fafafa', transition: 'all 0.2s' },
  unassignedHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  unassignedTitle: { fontSize: 13, fontWeight: 500, color: '#444' },
  unassignedHint: { fontSize: 11, color: '#5f6b58' },
  slotSection: { marginBottom: 8, borderRadius: 10, transition: 'all 0.2s' },
  slotOver: { border: '2px dashed #3B6D11', background: '#f0fdf4' },
  slotHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderRadius: 10, marginBottom: 6 },
  slotHeaderLeft: { display: 'flex', alignItems: 'center', gap: 6 },
  slotIcon: { fontSize: 16 },
  slotLabel: { fontSize: 14, fontWeight: 500 },
  slotCount: { color: '#fff', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 500 },
  slotHeaderRight: { display: 'flex', gap: 6 },
  slotNavBtn: { padding: '5px 10px', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 500, color: '#fff', cursor: 'pointer' },
  slotSaveBtn: { padding: '5px 10px', border: '1px solid #ddd', background: '#fff', borderRadius: 8, fontSize: 12, cursor: 'pointer', color: '#5f6b58' },
  slotTotals: { display: 'flex', gap: 6, padding: '4px 12px 8px', flexWrap: 'wrap' },
  slotEmpty: { textAlign: 'center', padding: '20px', color: '#5f6b58', fontSize: 13, background: '#fafafa', borderRadius: 8, marginBottom: 6, border: '2px dashed #eee', transition: 'all 0.2s' },
  slotEmptyOver: { background: '#EAF3DE', color: '#3B6D11', borderColor: '#3B6D11' },
  stopCard: { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', border: '1px solid #eee', borderRadius: 10, marginBottom: 6, background: '#fff', transition: 'opacity 0.2s' },
  stopDone: { opacity: 0.6 },
  stopPaid: { borderColor: '#86efac' },
  stopOwes: { borderColor: '#fca5a5' },
  stopNum: { width: 26, height: 26, borderRadius: '50%', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 500, flexShrink: 0, marginTop: 2 },
  stopInfo: { flex: 1, minWidth: 0 },
  stopName: { fontSize: 13, fontWeight: 500, color: '#1a1a1a' },
  stopAddr: { fontSize: 12, color: '#5f6b58', marginTop: 2 },
  stopNotes: { fontSize: 12, color: '#f59e0b', marginTop: 2 },
  paymentSummary: { fontSize: 12, color: '#5f6b58', marginTop: 4 },
  stopBtns: { display: 'flex', gap: 5, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' },
  actionBtn: { padding: '4px 8px', border: '1px solid #ddd', background: 'none', borderRadius: 20, fontSize: 11, cursor: 'pointer', color: '#5f6b58' },
  doneActive: { background: '#EAF3DE', color: '#3B6D11', borderColor: '#3B6D11' },
  payBtn: { borderColor: '#3B6D11', color: '#3B6D11' },
  stopActions: { display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 },
  iconBtn: { width: 28, height: 28, border: '1px solid #eee', background: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, color: '#5f6b58' },
  removeBtn: { color: '#dc2626', borderColor: '#fecaca' },
  endRouteBtn: { width: '100%', padding: '10px', border: 'none', background: '#1a1a1a', borderRadius: 8, fontSize: 12, fontWeight: 500, color: '#fff', cursor: 'pointer', marginTop: 6, marginBottom: 8 },
  clearAllBtn: { width: '100%', padding: '10px', border: '1px solid #fca5a5', background: 'none', borderRadius: 8, fontSize: 13, color: '#dc2626', cursor: 'pointer', marginTop: 8 },
  srOnly: { position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }
};
